import React, { useCallback, useEffect, useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Drawer as DrawerAntD, message, Result, Select, Table, Tag } from 'antd';
import moment, { Moment as MomentType } from 'moment';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { paymentStatusView } from './crossPaymentStatus';


import {
  downloadCrossTransactionsCsv,
  getCrossTransactionTotals,
  getCrossTransactions,
} from '../../../../hooks/interoperability';
import {
  Connection,
  CrossTransaction,
  CrossTransactionQuery,
  CrossTransactionTotals,
} from '../../../../types/responses/Interoperability';
import { TransactionDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { PartnerLogo } from '../../../molecules/PartnerLogo';
import {
  Chevron,
  ClickableRows,
  ListCard,
  TotalCard,
  TotalCardKey,
  TotalCardValue,
  TotalsStrip,
} from '../styled';
import {
  chargeDuration,
  chargePeriod,
  defaultPeriod,
  formatCount,
  formatEnergy,
  formatMoney,
  periodQuery,
  stationLabel,
} from '../transactionFormat';

const Filters = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const ExportButton = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 11.5px;

  &:disabled {
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

const Numeric = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const StationTitle = styled.div`
  color: #12263f;
`;

const StationCode = styled.div`
  color: #8a9099;
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const Origin = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChargingPill = styled.span`
  background: #e7f6ed;
  color: #1e7a45;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10.5px;
  margin-left: 6px;
`;

const Masked = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const { RangePicker } = DatePicker;

/** O Drawer do antd nao declara children no typing desta versao. */
const Drawer: any = DrawerAntD;

/**
 * Dez por pagina, como as outras listagens do modulo.
 *
 * A linha da recarga tem estacao, origem, usuario mascarado, periodo, duracao, energia, valor
 * e status: vinte dessas viram rolagem, e o paginador no rodape fica longe de quem le.
 */
const MaskFoot = styled.p`
  margin: 0;
  border-top: 1px solid #e9ebee;
  background: #fafbfc;
  padding: 9px 16px;
  font-size: 11px;
  color: #8a9099;
`;

const PAGE_SIZE = 10;

export type TransactionsTabProps = {
  tenantId: string;

  /** Parceiros com contrato, para o filtro de origem. */
  connections: Connection[];
};

/**
 * Aba Transações (MV2-1717).
 *
 * Quem recarregou nas minhas estações usando app de outro tenant. A
 * identificação do usuário já chega mascarada da API — não existe caminho nesta
 * tela que mostre a tag completa, porque o servidor nunca a envia.
 *
 * Os totais do rodapé vêm de consulta própria, sobre o período filtrado inteiro:
 * somar a página daria um "a faturar" que muda quando a pessoa navega.
 */
export const TransactionsTab: React.FC<TransactionsTabProps> = ({ tenantId, connections }) => {
  const [period] = useState(() => defaultPeriod());

  const [partnerTenantId, setPartnerTenantId] = useState('');
  const [from, setFrom] = useState(period.from.slice(0, 10));
  const [to, setTo] = useState(period.to.slice(0, 10));

  const [detail, setDetail] = useState<CrossTransaction | null>(null);

  const [charges, setCharges] = useState<CrossTransaction[]>([]);
  const [totals, setTotals] = useState<CrossTransactionTotals | null>(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** O dia final entra até 23:59:59, senão o filtro perde as recargas do dia. */
  const query: CrossTransactionQuery = {
    ...(partnerTenantId ? { partnerTenantId } : {}),
    ...periodQuery(from, to),
  };

  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const parsed: CrossTransactionQuery = JSON.parse(queryKey);

      const [pageResult, totalsResult] = await Promise.all([
        getCrossTransactions(tenantId, parsed, page, PAGE_SIZE),
        getCrossTransactionTotals(tenantId, parsed),
      ]);

      setCharges(pageResult?.content || []);
      setTotalElements(pageResult?.totalElements || 0);
      setTotals(totalsResult);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId, queryKey, page]);

  useEffect(() => {
    load();
  }, [load]);

  /** Trocar filtro volta para a primeira página: a atual pode não existir mais. */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      await downloadCrossTransactionsCsv(tenantId, query);
    } catch {
      // Aviso proprio, e nao o `loadError`: aquele e o estado de falha da CARGA, e o
      // render esconde a lista inteira atras dele. Falhar o download apagava da tela as
      // linhas que tinham carregado bem, com uma frase falsa sobre elas.
      message.error(T('Could not download the CSV. The list on screen is unchanged.'));
    } finally {
      setExporting(false);
    }
  };


  /**
   * Colunas no formato do antd Table, como o resto do dashboard.
   *
   * A tabela era `<table>` cru com Th/Td proprios, o que fazia esta aba nao parecer
   * nem do mesmo modulo que Conexoes. O `scroll={{ x: true }}` cobre a rolagem
   * horizontal que as dez colunas exigem — era o unico motivo do wrapper anterior.
   */
  const columns = [
    {
      title: T('Charge'),
      key: 'charge',
      render: (_: unknown, charge: CrossTransaction) => charge.transaction_id.slice(0, 8),
    },
    {
      title: T('Station'),
      key: 'station',
      render: (_: unknown, charge: CrossTransaction) => {
        const station = stationLabel(charge);

        return (
          <div>
            <StationTitle>{station.title}</StationTitle>
            {station.subtitle && <StationCode>{station.subtitle}</StationCode>}
          </div>
        );
      },
    },
    {
      title: T('Connector'),
      key: 'connector',
      render: (_: unknown, charge: CrossTransaction) => charge.connector_seq ?? '—',
    },
    {
      title: T('Origin tenant'),
      key: 'origin',
      render: (_: unknown, charge: CrossTransaction) => (
        <Origin>
          <PartnerLogo
            name={charge.origin_tenant_name || ''}
            alias={charge.origin_tenant_alias}
          />
          {charge.origin_tenant_name || '—'}
        </Origin>
      ),
    },
    {
      title: T('User (Tag)'),
      key: 'user',
      render: (_: unknown, charge: CrossTransaction) => (
        <Masked>{charge.masked_user || '—'}</Masked>
      ),
    },
    {
      title: T('Start - End'),
      key: 'period',
      render: (_: unknown, charge: CrossTransaction) => (
        <>
          {chargePeriod(charge)}
          {charge.in_progress && <ChargingPill>{T('in progress')}</ChargingPill>}
        </>
      ),
    },
    {
      title: T('Duration'),
      key: 'duration',
      render: (_: unknown, charge: CrossTransaction) => chargeDuration(charge),
    },
    {
      title: T('Energy (kWh)'),
      key: 'energy',
      align: 'right' as const,
      render: (_: unknown, charge: CrossTransaction) => (
        <Numeric>{formatEnergy(charge.energy_kwh)}</Numeric>
      ),
    },
    {
      title: T('Revenue'),
      key: 'revenue',
      align: 'right' as const,
      render: (_: unknown, charge: CrossTransaction) => (
        <Numeric>{formatMoney(charge.income)}</Numeric>
      ),
    },
    {
      /*
        Status na Tag do antd, com o mesmo rotulo e a mesma cor do financeiro. A
        coluna mostrava o valor cru do banco — "APPROVED", "PENDING" — enquanto o
        resto do dashboard mostra "Pago" e "Pendente" coloridos: mesmo dado, duas
        leituras no mesmo produto.
      */
      title: T('Payment'),
      key: 'payment',
      render: (_: unknown, charge: CrossTransaction) => {
        const status = paymentStatusView(charge.payment_status);

        if (!status) {
          return '—';
        }

        // Tag sempre, mesmo sem cor: o financeiro faz assim nos status que nao
        // constam do mapa de cores, e duas formas na mesma coluna se leem como
        // dois significados.
        return (
          <Tag color={status.color} style={{ marginRight: 0 }}>
            {status.label}
          </Tag>
        );
      },
    },
    {
      /* A seta que avisa que a linha abre detalhe, como Conexoes e API. */
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  return (
    <ClickableRows>
      {/*
        Totais no alto, como na aba Estacoes: numero que resume a tela se le antes da tabela.
        No rodape, so aparecia para quem rolava ate o fim.
      */}
      <TotalsStrip>
        <TotalCard>
          <TotalCardKey>{T('Charges')}</TotalCardKey>
          <TotalCardValue>{formatCount(totals?.session_count ?? null)}</TotalCardValue>
        </TotalCard>
        <TotalCard>
          <TotalCardKey>{T('Energy')}</TotalCardKey>
          <TotalCardValue>{formatEnergy(totals?.energy_kwh ?? null)} kWh</TotalCardValue>
        </TotalCard>
        <TotalCard>
          <TotalCardKey>{T('To invoice')}</TotalCardKey>
          <TotalCardValue>{formatMoney(totals?.income ?? null)}</TotalCardValue>
        </TotalCard>
      </TotalsStrip>

      <Filters>
        {/*
          Select do antd: ganha busca, que importa num tenant com dezenas de
          conexoes, e recebe a cor do tenant pelo createGlobalStyle — o <select>
          nativo ficava sem estado de foco visivel.
        */}
        <Select
          value={partnerTenantId}
          style={{ minWidth: 200 }}
          showSearch
          optionFilterProp="children"
          onChange={(value: string) => changeFilter(() => setPartnerTenantId(value))}
        >
          <Select.Option value="">{T('All partners')}</Select.Option>
          {connections.map((connection) => (
            <Select.Option key={connection.partner_tenant_id} value={connection.partner_tenant_id}>
              {connection.partner_name}
            </Select.Option>
          ))}
        </Select>

        {/*
          RangePicker do antd com formato DD/MM/YYYY, como o resto do dashboard. O
          input[type=date] nativo mostrava a data no formato do sistema operacional —
          quem usa o SO em ingles via mm/dd/yyyy no meio de um dash em portugues.
        */}
        <RangePicker
          format="DD/MM/YYYY"
          allowClear={false}
          value={[moment(from), moment(to)]}
          onChange={(range) =>
            changeFilter(() => {
              const [start, end] = (range || []) as MomentType[];

              if (start && end) {
                setFrom(start.format('YYYY-MM-DD'));
                setTo(end.format('YYYY-MM-DD'));
              }
            })
          }
        />

        <Spacer />

        <ExportButton type="button" onClick={exportCsv} disabled={exporting || !totalElements}>
          {exporting ? T('Preparing...') : T('Download CSV')}
        </ExportButton>
      </Filters>

      {loadError && (
        <Result
          status="warning"
          title={T('Could not load the charges.')}
          extra={
            <Button type="primary" onClick={load}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {loading && <ListSkeleton />}

      {!loading && !loadError && (
        <ListCard>
          <Table
            rowKey="transaction_id"
            locale={{ emptyText: T('No cross charges in this period.') }}
            pagination={false}
            columns={columns}
            dataSource={charges}
            scroll={{ x: true }}
            rowClassName="interop-row"
            onRow={(item: CrossTransaction) => ({
              onClick: () => setDetail(item),
            })}
          />

          {/* O mascaramento e do servidor, e a nota fica junto da lista onde ele aparece. */}
          <MaskFoot>{T('Users from other tenants are always masked')}</MaskFoot>

          {/*
            Paginador do dashboard, e nao um par de botoes proprio: da salto direto
            de pagina, que em dezenas de paginas de recarga importa. Ele e 1-based e
            a API e 0-based, entao a conversao fica aqui, na fronteira.
          */}
          <Pagination
            currentPage={page + 1}
            pageSize={PAGE_SIZE}
            totalCount={totalElements}
            onPageChange={(next: number) => setPage(next - 1)}
          />
        </ListCard>
      )}
      <Drawer
        width={420}
        visible={detail !== null}
        title={detail ? stationLabel(detail).title : ''}
        onClose={() => setDetail(null)}
      >
        {detail && <TransactionDrawerDetails charge={detail} />}
      </Drawer>
    </ClickableRows>
  );
};
